//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { PrimaryButton } from '@components/atoms/button/Button';
import { BodySecondaryText, SubtitleText } from '@components/atoms/text/Text';
import skippedRightsSvg from '@images/uia/uia-approve-rights.svg';
import { UIA_RIGHTS_FLOW_TRACKING_IDS } from '@uia/rights/constants/uiaRightsConstants';
import { getIsRenderedWithinAnotherWorkflow } from '@uia/rights/selectors/getUIARights';
import { withTracking } from '@utils/withTracking';

export function UIARightsSkipped({ onClose }: { onClose: () => void }) {
  const isRenderedWithinAnotherWorkflow = useSelector(
    getIsRenderedWithinAnotherWorkflow,
  );
  return (
    <OverlayWrapper>
      <RightsSkippedContainer>
        <RightsApprovedImage alt='rights skipped' src={skippedRightsSvg} />
        <SubtitleText>Rights Skipped</SubtitleText>
        <StyledText>
          The rights request has been skipped for this media. You're free to add
          some products and distribute this content.
        </StyledText>
      </RightsSkippedContainer>
      {!isRenderedWithinAnotherWorkflow && (
        <Footer>
          <FooterContainer>
            <FooterButtonContainer>
              <SubmitButton
                onClick={() => onClose()}
                trackingId={
                  UIA_RIGHTS_FLOW_TRACKING_IDS.SKIP_RIGHTS_DONE_BUTTON
                }
              >
                <SubmitContainer>Done</SubmitContainer>
              </SubmitButton>
            </FooterButtonContainer>
          </FooterContainer>
        </Footer>
      )}
    </OverlayWrapper>
  );
}

const OverlayWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  width: 100%;
`;

const RightsSkippedContainer = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: calc(${theme.spacings[1]} * 25); // 100px
  `,
);

const Footer = styled.footer`
  display: flex;
`;

const FooterContainer = styled.div`
  width: 100%;
`;

const FooterButtonContainer = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: flex-end;
    border-bottom: ${theme.borderWidths[1]} solid ${theme.colors.gray1};
    border-top: ${theme.borderWidths[1]} solid ${theme.colors.gray1};
    padding: ${theme.spacings[3]};
  `,
);

const SubmitButton = withTracking(
  styled(PrimaryButton)(
    ({ theme }) => `
    border-radius: ${theme.borderRadii[2]};
  `,
  ),
);

const SubmitContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StyledText = styled(BodySecondaryText)(
  ({ theme }) => `
    text-align: center;
    padding: 0 ${theme.spacings[7]};
  `,
);

const RightsApprovedImage = styled.img`
  width: 100px;
`;
